/**
 * Single client-side error sink. Today this is `console.error` with a
 * stable code prefix and a typed context object so the browser console
 * shows a useful first line and developers can grep for a given code
 * across logs without staring at the surrounding text. Future:
 * forward to an observability service.
 *
 * Why route every catch through this rather than calling `console.error`
 * directly: code-level grouping (so a multi-GB upload failure isn't lost
 * in the noise of viewer-dispose warnings), structured context (errors
 * carry the user-facing identifiers — caseId, slotKey, uploadId —
 * needed to triage), and a single chokepoint when we ship a real
 * observability backend.
 */
export type ErrorContext = Record<string, unknown>;

export function logError(code: string, message: string, context?: ErrorContext): void {
    const err = context?.error ?? context?.cause;
    if (err instanceof Error) {
        console.error(`[${code}] ${message}`, context ?? {}, err);
    } else {
        console.error(`[${code}] ${message}`, context ?? {});
    }
}

/**
 * Stable error identifiers. Add new ones here instead of inlining
 * string literals — keeps the catalogue searchable.
 */
export const errorIds = {
    EAGER_UPLOAD_FAILED: 'eager_upload_failed',
    WIZARD_SUBMIT_FAILED: 'wizard_submit_failed',
    WIZARD_INITIAL_JOB_FAILED: 'wizard_initial_job_failed',
    VIEWER_PREVIEW_PARSE_FAILED: 'viewer_preview_parse_failed',
    VIEWER_DISPOSE_FAILED: 'viewer_dispose_failed',
    DICOM_WORKER_METADATA_FAILED: 'dicom_worker_metadata_failed',
    UDI_DATAMATRIX_RENDER_FAILED: 'udi_datamatrix_render_failed',
    TENANT_CLAIMS_LOAD_FAILED: 'tenant_claims_load_failed',
    TENANT_CLAIMS_REFRESH_FAILED: 'tenant_claims_refresh_failed',
    TENANT_CONFIRMATION_UNAVAILABLE: 'tenant_confirmation_unavailable',
    TENANT_LIST_UNAVAILABLE: 'tenant_list_unavailable',
    TENANT_CREATE_CLAIM_REFRESH_FAILED: 'tenant_create_claim_refresh_failed',
    INVITATION_CLAIM_REFRESH_FAILED: 'invitation_claim_refresh_failed',
    REFRESH_TOKEN_REVOKE_FAILED: 'refresh_token_revoke_failed',
    VERIFICATION_EMAIL_REQUEST_FAILED: 'verification_email_request_failed',
    EMAIL_VERIFICATION_UNPROVABLE: 'email_verification_unprovable',
    MARKETING_OPT_IN_SAVE_FAILED: 'marketing_opt_in_save_failed',
    CHECKOUT_SESSION_FAILED: 'checkout_session_failed',
    STRIPE_JS_LOAD_FAILED: 'stripe_js_load_failed',
    DEVICE_USE_GATE_SUBMIT_FAILED: 'device_use_gate_submit_failed',
    PROFILE_SAVE_FAILED: 'profile_save_failed',
    PROFILE_GUARD_UNAVAILABLE: 'profile_guard_unavailable',
    WORKFLOW_SAVE_FORBIDDEN: 'workflow_save_forbidden',
    EMAIL_CHANGE_FAILED: 'email_change_failed',
    CHECKOUT_CONFIRM_FAILED: 'checkout_confirm_failed',
} as const;

export type ErrorId = (typeof errorIds)[keyof typeof errorIds];
